import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import { useTheme } from "../../context/ThemeContext";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useRolesStore } from "../../store/admin/rolesStore";
import api from "../../utils/api";

interface ProfileFormData {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  phone: string;
  bio: string;
  location: string;
  website: string;
}

export default function ProfilePage() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // «Alertas» se mudó a Configuración → Alertas (Daniel, 26-oct).
  const [activeTab, setActiveTab] = useState<"profile" | "activity" | "settings">("profile");
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    phone: "",
    bio: "",
    location: "",
    website: "",
  });
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        username: user.username || "",
        phone: user.phone || "",
        bio: user.bio || "",
        location: user.location || "",
        website: user.website || "",
      });
    }
  }, [user]);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      console.error("No user ID available; cannot update profile.");
      return;
    }

    setIsSaving(true);
    try {
      const requestBody = {
        userID: user.id,
        user: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          username: formData.username,
          bio: formData.bio,
          location: formData.location,
          website: formData.website,
          ...(formData.phone && formData.phone.trim() !== ""
            ? { phone: formData.phone }
            : {}),
        },
      };

      await api.put("/user", requestBody, { timeout: 60000 });

      setUser({ ...user, ...formData });
      localStorage.setItem("lastProfileUpdate", Date.now().toString());
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        username: user.username || "",
        phone: user.phone || "",
        bio: user.bio || "",
        location: user.location || "",
        website: user.website || "",
      });
    }
    setIsEditing(false);
  };

  const initials =
    formData.first_name && formData.last_name
      ? `${formData.first_name.charAt(0).toUpperCase()}${formData.last_name.charAt(0).toUpperCase()}`
      : "DU";

  const displayName =
    formData.first_name && formData.last_name
      ? `${formData.first_name} ${formData.last_name}`
      : formData.email || "User";

  const bgColor = isDarkMode ? "bg-[#121212]" : "bg-gray-50";
  const cardBg = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-dark-surface" : "border-gray-200";
  const inputBg = isDarkMode ? "bg-dark-surface" : "bg-gray-100";
  const hoverBg = isDarkMode ? "hover:bg-dark-surface" : "hover:bg-gray-100";

  const companies = useCompaniesStore((state) => state.allCompanies);
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  
  const roles = useRolesStore((state) => state.roles);
  const fetchRoles = useRolesStore((state) => state.fetchRoles);

  useEffect(() => {
    if (companies.length === 0) {
      fetchAllCompanies();
    }
  }, [companies.length, fetchAllCompanies]);

  useEffect(() => {
    if (roles.length === 0) {
      fetchRoles();
    }
  }, [roles.length, fetchRoles]);

  const userCompany = companies.find((c) => c.id === companyUser);
  const userRole = roles.find((r) => r.id === user?.role_id);

  const companyDisplayName = userCompany?.name || "Company";
  let roleDisplayName = userRole?.name || "Role";
  
  switch (roleDisplayName) {
    case "admin reseller":
      roleDisplayName = "Reseller";
      break;
    case "monitor":
      roleDisplayName = "Monitor";
      break;
    case "admin":
      roleDisplayName = "Admin";
      break;
    case "driver":
      roleDisplayName = "Driver";
      break;
    default:
      break;
  }

  

  return (
    <div className={`h-full overflow-y-auto ${bgColor}`}>
      {/* Cover Photo */}
      <div className="relative h-32 sm:h-48 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600">
        <div className="absolute inset-0 bg-black/10"></div>
        {/* Portada: edición deshabilitada por ahora
        <button
          className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/40 hover:bg-black/60 text-white text-sm rounded-lg backdrop-blur-sm transition-colors flex items-center gap-1.5 z-10"
          onClick={() => {}}
        >
          <i className="ri-camera-line"></i>
          <span className="hidden sm:inline">{t("profile.editCover", "Edit Cover")}</span>
        </button>
        */}
      </div>

      {/* Profile Info Section */}
      <div className="max-w-4xl mx-auto px-4">
        <div className={`${cardBg} rounded-xl shadow-lg -mt-12 sm:-mt-16 relative z-10`}>
          {/* Avatar & Name Row */}
          <div className="px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0 mx-auto sm:mx-0">
                <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-primary-500 to-primary-400 rounded-full flex items-center justify-center border-4 border-white dark:border-dark-bg shadow-xl">
                  <span className="text-2xl sm:text-4xl font-bold text-gray-900">{initials}</span>
                </div>
                {/* Avatar: edición deshabilitada por ahora
                <button
                  className="absolute bottom-1 right-1 w-8 h-8 bg-primary-500 hover:bg-primary-400 rounded-full flex items-center justify-center shadow-md transition-colors"
                >
                  <i className="ri-camera-line text-gray-900 text-sm"></i>
                </button>
                */}
              </div>

              {/* Name & Info */}
              <div className="flex-1 text-center sm:text-left">
                <h2 className={`text-xl sm:text-2xl font-bold ${textPrimary}`}>{displayName}</h2>
                {formData.username && (
                  <p className={`${textSecondary} text-sm`}>@{formData.username}</p>
                )}
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-1">
                  <i className={`ri-building-2-line ${textSecondary}`}></i>
                  <span className={`${textSecondary} text-sm`}>
                    {companyDisplayName} • {roleDisplayName}
                  </span>
                </div>
              </div>

              {/* Actions */}
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        className={`px-4 py-2 ${inputBg} ${textPrimary} rounded-lg text-sm font-medium transition-colors ${hoverBg}`}
                      >
                        {t("common.cancel", "Cancel")}
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {isSaving && <i className="ri-loader-4-line animate-spin"></i>}
                        {t("common.save", "Save")}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <i className="ri-edit-line"></i>
                      {t("profile.editProfile", "Edit Profile")}
                    </button>
                  )}
                </div>
            </div>

            {/* Bio */}
            <div className="mt-4">
              {isEditing ? (
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleInputChange("bio", e.target.value)}
                  placeholder={t("profile.bioPlaceholder", "Tell us about yourself...")}
                  className={`w-full ${inputBg} ${textPrimary} rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 border ${borderColor}`}
                  rows={3}
                />
              ) : (
                <p className={`${textSecondary} text-sm`}>
                  {formData.bio || t("profile.noBio", "No bio yet. Click edit to add one!")}
                </p>
              )}
            </div>

            {/* Ubicación y sitio web */}
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t ${borderColor}`}
            >
              <div className="flex flex-col gap-1.5 min-w-0 w-full">
                <label
                  className={`text-sm font-medium ${textSecondary} flex items-center gap-2`}
                >
                  <i className="ri-map-pin-line"></i>
                  {t("profile.location", "Ubicación")}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    placeholder={t("profile.locationPlaceholder", "Ubicación")}
                    className={`w-full ${inputBg} ${textPrimary} rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border ${borderColor}`}
                  />
                ) : (
                  <p className={`${textPrimary} text-sm py-0.5 break-words`}>
                    {formData.location || (
                      <span className={textSecondary}>
                        {t("profile.noLocation", "No especificada")}
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5 min-w-0 w-full">
                <label
                  className={`text-sm font-medium ${textSecondary} flex items-center gap-2`}
                >
                  <i className="ri-links-line"></i>
                  {t("profile.website", "Sitio web")}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => handleInputChange("website", e.target.value)}
                    placeholder={t("profile.websitePlaceholder", "https://ejemplo.com")}
                    className={`w-full ${inputBg} ${textPrimary} rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border ${borderColor}`}
                  />
                ) : formData.website ? (
                  <a
                    href={formData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 text-sm hover:underline break-all py-0.5"
                  >
                    {formData.website.replace(/^https?:\/\//, "")}
                  </a>
                ) : (
                  <p className={`${textSecondary} text-sm py-0.5`}>
                    {t("profile.noWebsite", "Sin sitio web")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`${cardBg} rounded-xl shadow-lg mt-4 overflow-hidden`}>
          <div className={`flex border-b ${borderColor}`}>
            {(["profile", "activity", "settings"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab
                    ? `${textPrimary}`
                    : `${textSecondary} ${hoverBg}`
                }`}
              >
                {t(`profile.tabs.${tab}`, tab.charAt(0).toUpperCase() + tab.slice(1))}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"></div>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {activeTab === "profile" && (
              <ProfileDetailsTab
                formData={formData}
                isEditing={isEditing}
                onInputChange={handleInputChange}
                isDarkMode={isDarkMode}
              />
            )}
            {activeTab === "activity" && (
              <ActivityTab isDarkMode={isDarkMode} />
            )}
            {activeTab === "settings" && (
              <SettingsTab isDarkMode={isDarkMode} />
            )}
          </div>
        </div>
      </div>

      {/* Bottom Spacing */}
      <div className="h-8"></div>
    </div>
  );
}

interface ProfileDetailsTabProps {
  formData: ProfileFormData;
  isEditing: boolean;
  onInputChange: (field: keyof ProfileFormData, value: string) => void;
  isDarkMode: boolean;
}

function ProfileDetailsTab({ formData, isEditing, onInputChange, isDarkMode }: ProfileDetailsTabProps) {
  const { t } = useTranslation("common");
  
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputBg = isDarkMode ? "bg-dark-surface" : "bg-gray-100";
  const borderColor = isDarkMode ? "border-dark-surface" : "border-gray-200";

  const fields: { key: keyof ProfileFormData; label: string; icon: string; type?: string }[] = [
    { key: "first_name", label: t("profile.firstName", "First Name"), icon: "ri-user-line" },
    { key: "last_name", label: t("profile.lastName", "Last Name"), icon: "ri-user-line" },
    { key: "email", label: t("profile.email", "Email"), icon: "ri-mail-line", type: "email" },
    { key: "username", label: t("profile.username", "Username"), icon: "ri-at-line" },
    { key: "phone", label: t("profile.phone", "Phone"), icon: "ri-phone-line", type: "tel" },
  ];

  return (
    <div className="space-y-4">
      <h3 className={`text-lg font-semibold ${textPrimary} mb-4`}>
        {t("profile.personalInfo", "Personal Information")}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(({ key, label, icon, type = "text" }) => (
          <div key={key} className="space-y-1.5">
            <label className={`text-sm font-medium ${textSecondary} flex items-center gap-2`}>
              <i className={icon}></i>
              {label}
            </label>
            {isEditing ? (
              <input
                type={type}
                value={formData[key]}
                onChange={(e) => onInputChange(key, e.target.value)}
                className={`w-full ${inputBg} ${textPrimary} rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 border ${borderColor}`}
              />
            ) : (
              <p className={`${textPrimary} text-sm py-2.5`}>
                {formData[key] || <span className={textSecondary}>—</span>}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface ActivityTabProps {
  isDarkMode: boolean;
}

function ActivityTab({ isDarkMode }: ActivityTabProps) {
  const { t } = useTranslation("common");
  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const cardBg = isDarkMode ? "bg-dark-surface" : "";

  const formatTimeAgo = (timestamp: string | null) => {
    if (!timestamp) return null;
    
    const date = new Date(parseInt(timestamp));
    const now = Date.now();
    const diff = now - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t("profile.activity.justNow", "Just now");
    if (minutes < 60) return `${minutes} ${t("profile.activity.minutesAgo", "minute(s) ago")}`;
    if (hours < 24) return `${hours} ${t("profile.activity.hoursAgo", "hour(s) ago")}`;
    if (days < 7) return `${days} ${t("profile.activity.daysAgo", "day(s) ago")}`;
    
    return date.toLocaleDateString();
  };

  const lastLoginTime = localStorage.getItem("lastLoginTime");
  const lastProfileUpdate = localStorage.getItem("lastProfileUpdate");

  const activities = [
    lastLoginTime ? {
      icon: "ri-login-box-line",
      text: t("profile.activity.login", "Logged in"),
      time: formatTimeAgo(lastLoginTime),
    } : null,
    lastProfileUpdate ? {
      icon: "ri-user-settings-line",
      text: t("profile.activity.profileUpdate", "Profile updated"),
      time: formatTimeAgo(lastProfileUpdate),
    } : null,
  ].filter(Boolean) as { icon: string; text: string; time: string | null }[];

  return (
    <div className="space-y-4">
      <h3 className={`text-lg font-semibold ${textPrimary} mb-4`}>
        {t("profile.recentActivity", "Recent Activity")}
      </h3>
      <div className="space-y-3">
        {activities.length > 0 ? (
          activities.map((activity, index) => (
            <div
              key={index}
              className={`${cardBg} rounded-lg p-4 flex items-center gap-4 `}
            >
              <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                <i className={`${activity.icon} text-primary-500 text-lg`}></i>
              </div>
              <div className="flex-1">
                <p className={`${textPrimary} text-sm font-medium`}>{activity.text}</p>
                <p className={`${textSecondary} text-xs`}>{activity.time}</p>
              </div>
            </div>
          ))
        ) : (
          <div className={`${cardBg} rounded-lg p-4 text-center`}>
            <i className={`ri-history-line text-2xl ${textSecondary}`}></i>
            <p className={`${textSecondary} text-sm mt-2`}>
              {t("profile.activity.noActivity", "No recent activity")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface SettingsTabProps {
  isDarkMode: boolean;
}

function SettingsTab({ isDarkMode }: SettingsTabProps) {
  const { t, i18n } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showAppearanceDropdown, setShowAppearanceDropdown] = useState(false);
  const currentLanguage = i18n.language;

  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const cardBg = isDarkMode ? "bg-dark-surface" : "";
  const hoverBg = isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-200";
  const dropdownBg = isDarkMode ? "bg-dark-card" : "bg-white";

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("@language", lang);
    //setShowLanguageDropdown(false);
  };

  const handleAppearanceChange = (mode: "light" | "dark" | "system") => {
    setTheme(mode);
    setShowAppearanceDropdown(false);
  };

  const settingsOptions = [
    // { 
    //   icon: "ri-notification-3-line", 
    //   label: t("profile.settings.notifications", "Notifications"),
    //   description: t("profile.settings.notificationsDesc", "Manage your notification preferences"), 
    //   key: "notifications" 
    // },
    // { 
    //   icon: "ri-lock-line", 
    //   label: t("profile.settings.security", "Security"), 
    //   description: t("profile.settings.securityDesc", "Password and two-factor authentication"), 
    //   key: "security" 
    // },
    { icon: "ri-global-line", 
      label: t("profile.settings.language", "Language"), 
      description: t("profile.settings.languageDesc", "Change your preferred language"), 
      key: "language" 
    },
    { icon: "ri-palette-line", 
      label: t("profile.settings.appearance", "Appearance"), 
      description: t("profile.settings.appearanceDesc", "Customize how the app looks"), 
      key: "appearance" 
    },
  ];

  return (
    <div className="space-y-4">
      <h3 className={`text-lg font-semibold ${textPrimary} mb-4`}>
        {t("profile.accountSettings", "Account Settings")}
      </h3>
      <div className="space-y-2">
        {settingsOptions.map((option, index) => (
          <div key={index} className="relative">
            <button
              onClick={() => {
                if (option.key === "language") setShowLanguageDropdown(!showLanguageDropdown);
                if (option.key === "appearance") setShowAppearanceDropdown(!showAppearanceDropdown);
              }}
              className={`w-full ${cardBg} ${hoverBg} rounded-lg p-4 flex items-center gap-4 transition-colors text-left`}
            >
              <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                <i className={`${option.icon} text-primary-500 text-lg`}></i>
              </div>
              <div className="flex-1">
                <p className={`${textPrimary} text-sm font-medium`}>{option.label}</p>
                <p className={`${textSecondary} text-xs`}>{option.description}</p>
              </div>
              {option.key === "language" ? (
                <i className={`ri-arrow-${showLanguageDropdown ? 'up' : 'down'}-s-line ${textSecondary} text-xl transition-transform`}></i>
              ) : option.key === "appearance" ? (
                <i className={`ri-arrow-${showAppearanceDropdown ? 'up' : 'down'}-s-line ${textSecondary} text-xl transition-transform`}></i>
              ) : (
                <i className={`ri-arrow-right-s-line ${textSecondary} text-xl`}></i>
              )}
            </button>
            
            {/* Language Dropdown */}
            {option.key === "language" && showLanguageDropdown && (
              <div className={`mt-2 ${dropdownBg} rounded-lg shadow-lg overflow-hidden border ${isDarkMode ? 'border-[#444]' : 'border-gray-200'}`}>
                <button
                  onClick={() => handleLanguageChange("en")}
                  className={`w-full px-4 py-3 flex items-center gap-3 ${hoverBg} transition-colors`}
                >
                  <span className="text-lg">🇺🇸</span>
                  <span className={`${textPrimary} text-sm font-medium`}>English</span>
                  {currentLanguage === "en" && (
                    <i className="ri-check-line text-primary-500 ml-auto"></i>
                  )}
                </button>
                <button
                  onClick={() => handleLanguageChange("es")}
                  className={`w-full px-4 py-3 flex items-center gap-3 ${hoverBg} transition-colors`}
                >
                  <span className="text-lg">🇪🇸</span>
                  <span className={`${textPrimary} text-sm font-medium`}>Español</span>
                  {currentLanguage === "es" && (
                    <i className="ri-check-line text-primary-500 ml-auto"></i>
                  )}
                </button>
              </div>
            )}
            
            {/* Appearance Dropdown */}
            {option.key === "appearance" && showAppearanceDropdown && (
              <div className={`mt-2 ${dropdownBg} rounded-lg shadow-lg overflow-hidden border ${isDarkMode ? 'border-[#444]' : 'border-gray-200'}`}>
                <button
                  onClick={() => handleAppearanceChange("light")}
                  className={`w-full px-4 py-3 flex items-center gap-3 ${hoverBg} transition-colors`}
                >
                  <i className="ri-sun-line text-lg text-yellow-500"></i>
                  <span className={`${textPrimary} text-sm font-medium`}>Modo claro</span>
                  {themeMode === "light" && (
                    <i className="ri-check-line text-primary-500 ml-auto"></i>
                  )}
                </button>
                <button
                  onClick={() => handleAppearanceChange("dark")}
                  className={`w-full px-4 py-3 flex items-center gap-3 ${hoverBg} transition-colors`}
                >
                  <i className="ri-moon-line text-lg text-purple-500"></i>
                  <span className={`${textPrimary} text-sm font-medium`}>Modo oscuro</span>
                  {themeMode === "dark" && (
                    <i className="ri-check-line text-primary-500 ml-auto"></i>
                  )}
                </button>
                <button
                  onClick={() => handleAppearanceChange("system")}
                  className={`w-full px-4 py-3 flex items-center gap-3 ${hoverBg} transition-colors`}
                >
                  <i className="ri-computer-line text-lg text-blue-500"></i>
                  <span className={`${textPrimary} text-sm font-medium`}>Igual que el sistema</span>
                  {themeMode === "system" && (
                    <i className="ri-check-line text-primary-500 ml-auto"></i>
                  )}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
